import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";

import { get } from "@/api";
import { CartAvailablePromotion } from "@/modules/cart";
import { useCurrentOrderDetails } from "@/modules/cart/useCartDetails";
import { Models } from "api/types";
import { Icon } from "atoms/icon/Icon";
import CheckSmall from "atoms/icon/icons/ic_check_small_ol.svg";
import ChevronRight from "atoms/icon/icons/ic_chevron_right_small_ol.svg";
import PromoIconDot from "atoms/icon/icons/ic_offers+1_nav_ol.svg";
import PromoIcon from "atoms/icon/icons/ic_offers_nav_ol.svg";
import WarningIcon from "atoms/icon/icons/warning_triangle.svg";
import { TextEntry } from "molecules/cart-promotions/modal/list/text-entry/TextEntry";
import { ListModal } from "molecules/modal-window/list-modal/ListModal";
import { BuyXGetYCheckoutModal } from "organisms/buy-x-get-y-checkout-modal/BuyXGetYCheckoutModal";
import { PromotionCheckoutModal } from "organisms/promotion-checkout-modal/PromotionCheckoutModal";
import { isBuyXGetYDiscount } from "services/HelperService";

type AvailablePromotionsProps = {
  rewardPromos?: Models<"RewardPromo">[] | null;
  onPromoSubmit: (promo: string, shouldLoad?: boolean) => void;
  setCoupon: (promo: string) => void;
  errorMsg?: string;
  coupon: string;
  onDelete: () => void;
  onRewardSubmit: (rewardId: string) => void;
  rewardId: string;
  discountId?: string;
  setRewardId: (rewardId: string) => void;
  setDiscountId: (discountId: string) => void;
  successMsg?: string;
  externalPromoAmountOff?: number | 0;
  appliedPromoTitle?: string | null;
  successMsgOnDelete?: boolean | null;
  membershipDiscount?: Models<"GetMembershipDetailsResult"> | undefined;
  isMembershipDiscountApplied?: boolean;
  loading?: boolean;
  checkoutPromoModalOpen: boolean;
  setCheckoutPromoModalOpen: (open: boolean) => void;
  checkoutButtonClick: () => void;
  checkoutButtonLabel: string;
  hasMoreDiscounts: boolean;
  buyXgetYPromotionTitle?: string;
  buyXgetYPromoCode: string | null | undefined;
  locationName?: string;
  openPromotionsModal: boolean; // Direct control of modal state
  togglePromotionModal: (open: boolean) => void; // Direct modal state setter
  unavailableMembership?: Models<"GetMembershipDetailsResult">;
  hasInvalidMembershipApplied?: boolean;
};
const PromotionModal = ({
  children,
  openModal,
  onClose,
}: {
  children: React.ReactNode;
  openModal: boolean;
  onClose: () => void;
}) => {
  return (
    <ListModal
      open={openModal}
      size="fullscreen"
      title="Promotions"
      onClose={onClose}
      firstButtonLabel="Apply & continue checkout"
      onFirstButtonClick={onClose}
    >
      <div className="flex flex-col items-center px-4 py-3">
        <div className="pb-[18px] pt-[6px] text-secondary">
          Select a promotion or enter a promo code
        </div>
        {children}
      </div>
    </ListModal>
  );
};
export const AvailablePromotions = (props: AvailablePromotionsProps) => {
  const [customPromo, setCustomPromo] = useState<string>("");
  const [resetTextEntry, setResetTextEntry] = useState<boolean>(false);
  const [alreadyAppliedPromo, setAlreadyAppliedPromo] = useState("");
  const refContainer = useRef<null | HTMLDivElement>(null);
  const membershipDiscountIsApplied = props.isMembershipDiscountApplied;
  const couponIsFromAvailablePromotionsList =
    props.rewardPromos &&
    props.rewardPromos.some(
      (p) =>
        (props.coupon == p.promoCode && props.discountId == p.discountId) ||
        props.discountId == p.discountId
    );

  const { data: cartDetails } = useCurrentOrderDetails();

  const cartPreFeeTotal = cartDetails?.preFeeTotal ?? 0;

  useEffect(() => {
    if (!props.loading && customPromo) {
      const inputElement = refContainer.current?.querySelector("input");
      if (inputElement) {
        inputElement.blur();
      }
      setCustomPromo("");
    }
  }, [props.loading]);
  useEffect(() => {
    // Auto-scroll behavior for textEntry
    refContainer.current?.scrollIntoView({ behavior: "smooth" });
  });
  useEffect(() => {
    if (props.checkoutPromoModalOpen) {
      if (props.coupon) {
        setAlreadyAppliedPromo(props.coupon);
      }
    }
  }, [props.checkoutPromoModalOpen, props.coupon]);

  const promosAvailableTitle = (title: string) => {
    // if title is too long, cut it off, add ellipsis, and add "applied!" to the end
    // othrwise just return title with "applied!" to the end.
    if (
      title.substring(title.lastIndexOf(" ") + 1).toLowerCase() == "applied" ||
      title.substring(title.lastIndexOf(" ") + 1).toLowerCase() == "applied!"
    ) {
      title = title.substring(0, title.lastIndexOf(" "));
    }
    if (title.length > 59) {
      if (
        title.lastIndexOf(".") === title.length - 1 ||
        title.lastIndexOf("!") === title.length - 1
      ) {
        return (
          title.substring(0, title.length - 1).substring(0, 59) +
          "..." +
          " applied!"
        );
      }
      return title.substring(0, 59) + "..." + " applied!";
    } else {
      if (
        title.lastIndexOf(".") === title.length - 1 ||
        title.lastIndexOf("!") === title.length - 1
      ) {
        return (title = title.substring(0, title.length - 1) + " applied!");
      }
      return title + " applied!";
    }
  };

  const getIcon = () => {
    // Show warning if there's a location issue (promo code error)
    if (props.locationName) return WarningIcon;

    if ((props.coupon && props.successMsg) || membershipDiscountIsApplied) {
      return CheckSmall;
    }

    if (props.rewardPromos && props.rewardPromos.length > 0)
      return PromoIconDot;

    return PromoIcon;
  };

  const getPromoTitle = () => {
    if (props.coupon && props.successMsg && props.appliedPromoTitle) {
      return promosAvailableTitle(props.appliedPromoTitle);
    }

    if (props.coupon && props.successMsg) {
      return promosAvailableTitle(props.successMsg);
    }

    if (membershipDiscountIsApplied || props.unavailableMembership) {
      return "Membership pricing applied!";
    }

    if (props.rewardPromos && props.rewardPromos.length > 0) {
      return "You have available promotions!";
    }

    return "Apply a promo code";
  };

  const promosAvailable = (
    <div
      className="flex w-full items-center justify-between rounded p-3"
      onClick={() => {
        props.togglePromotionModal(true);
      }}
    >
      <div className="flex items-center gap-3">
        <Icon
          svg={getIcon()}
          color={
            (props.coupon && props.successMsg) || membershipDiscountIsApplied
              ? "successGreen"
              : "secondaryText"
          }
          size="small"
        />
        <div className="flex flex-col gap-1">
          <div className="float-left flex text-bold2">
            <span className="line-clamp-2 inline-block">{getPromoTitle()}</span>
          </div>
          <div className="text-body2 text-secondary">
            {props.rewardPromos && props.rewardPromos.length > 0
              ? "Tap to view all available promotions"
              : "Tap to apply your code"}
          </div>
        </div>
      </div>
      <Icon svg={ChevronRight} color="brandColor" size="xsmall" />
    </div>
  );

  const removePromo = () => {
    props.onDelete();
  };

  const resetPromoAndReward = () => {
    props.setCoupon("");
    props.setRewardId("");
    props.setDiscountId("");
  };

  const handlePromoSubmit = (promoCode: string, shouldLoad?: boolean) => {
    resetPromoAndReward();
    props.setCoupon(promoCode);
    props.onPromoSubmit(promoCode, shouldLoad);
  };

  const handleRewardSubmit = (rewardId: string) => {
    resetPromoAndReward();
    props.setRewardId(rewardId);
    props.onRewardSubmit(rewardId);
  };

  useEffect(() => {
    if (props.successMsgOnDelete) {
      resetPromoAndReward();
    }
  }, [props.successMsgOnDelete, resetPromoAndReward]);

  const memberShipPromotion = props.membershipDiscount?.membershipName &&
    props.membershipDiscount.totalDiscount && (
      <CartAvailablePromotion
        cartPreFeeTotal={cartPreFeeTotal}
        textButton={!membershipDiscountIsApplied ? "APPLY" : ""}
        isMembership={true}
        onClick={removePromo}
        title={props.membershipDiscount.membershipName + " membership pricing"}
        totalDiscount={props.membershipDiscount.totalDiscount ?? undefined}
        isHighlighted={
          membershipDiscountIsApplied && !couponIsFromAvailablePromotionsList
        }
        appliesToMembership={!!props.membershipDiscount.appliesToMembership}
        locationName={props.locationName}
      />
    );
  const unavailableMembershipBenefit = props.locationName &&
    props.unavailableMembership &&
    !props.appliedPromoTitle && (
      <CartAvailablePromotion
        cartPreFeeTotal={cartPreFeeTotal}
        textButton="REMOVE"
        isMembership={true}
        onClick={removePromo}
        title={
          props.unavailableMembership.membershipName + " membership pricing"
        }
        totalDiscount={props.unavailableMembership.totalDiscount ?? undefined}
        isHighlighted={true}
        appliesToMembership={!!props.unavailableMembership.appliesToMembership}
        locationName={props.locationName}
      />
    );
  const maxDiscountPromo = props?.rewardPromos?.reduce(
    (max, current) =>
      current.totalDiscount > max.totalDiscount ? current : max,
    props.rewardPromos[0]
  );
  const memberShipHasMaxDiscount =
    (props?.membershipDiscount?.totalDiscount || 0) >
    (maxDiscountPromo?.totalDiscount || 0);
  const discountAndRewards = (reminder?: boolean) => (
    <>
      {reminder && memberShipHasMaxDiscount && memberShipPromotion}
      {props.rewardPromos &&
        (!reminder
          ? props.rewardPromos
          : memberShipHasMaxDiscount
          ? []
          : [{ ...maxDiscountPromo }]
        ).map((p, i) => {
          const isFreeServicePromo =
            p.discount?.percentOff === 1 &&
            !isBuyXGetYDiscount(p.discount) &&
            p.discount.inclusions?.some(
              (inclusion) => inclusion.membershipId !== null
            );

          return (
            <div key={`${p.rewardId ?? p.promotionId}_${i}`} className="w-full">
              <CartAvailablePromotion
                cartPreFeeTotal={cartPreFeeTotal}
                textButton={
                  props.successMsg &&
                  (props.coupon == p.promoCode ||
                    props.rewardId == p.rewardId ||
                    props.discountId == p.discountId)
                    ? "REMOVE"
                    : "APPLY"
                }
                onClick={() => {
                  if (
                    props.successMsg &&
                    (props.coupon == p.promoCode ||
                      props.discountId == p.discountId)
                  ) {
                    if (alreadyAppliedPromo && reminder) {
                      handlePromoSubmit(alreadyAppliedPromo);
                    } else {
                      removePromo();
                    }
                  } else {
                    if (p && p.rewardId) {
                      handleRewardSubmit(p.rewardId);
                    } else if (p && p.promoCode) {
                      handlePromoSubmit(p.promoCode);
                    }
                  }
                }}
                title={isFreeServicePromo ? "Free service" : p.title}
                totalDiscount={p.totalDiscount}
                expiresOn={p.expiresOn}
                isHighlighted={
                  props.successMsg &&
                  (props.coupon == p.promoCode ||
                    props.discountId == p.discountId)
                }
                isReward={p.rewardId ? true : false}
                appliesToMembership={!!p.appliesToMembership}
                minimumCartSpendAmount={p.minimumCartSpendAmount}
                requiresMinimumCartSpend={p.hasMinimumCartSpendRequirement}
                promotion={p as Models<"RewardPromo">}
              />
            </div>
          );
        })}
    </>
  );
  return (
    <div>
      {promosAvailable}
      <PromotionModal
        openModal={props.openPromotionsModal}
        onClose={() => props.togglePromotionModal(false)}
      >
        <div className="flex w-full flex-col gap-3">
          {memberShipPromotion}
          {unavailableMembershipBenefit}
          {discountAndRewards()}
          {props.coupon &&
            props.successMsg &&
            !couponIsFromAvailablePromotionsList && (
              <CartAvailablePromotion
                cartPreFeeTotal={cartPreFeeTotal}
                textButton="REMOVE"
                onClick={removePromo}
                title={props.successMsg}
                totalDiscount={props.externalPromoAmountOff}
                isHighlighted={true}
                locationName={
                  props.locationName ? props.locationName : undefined
                }
              />
            )}
          {(!props.coupon ||
            !props.successMsg ||
            props.locationName ||
            (props.coupon && couponIsFromAvailablePromotionsList)) && (
            <div ref={refContainer}>
              <TextEntry
                buttonText={customPromo || resetTextEntry ? "APPLY" : "REMOVE"}
                errorMsg={
                  !couponIsFromAvailablePromotionsList &&
                  !resetTextEntry &&
                  props.errorMsg &&
                  !props.successMsg &&
                  !props.loading
                    ? props.errorMsg
                    : undefined
                }
                value={customPromo}
                loading={props.loading}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  props.errorMsg ? setResetTextEntry(true) : "";
                  e.target.value.length > 0
                    ? setCustomPromo(e.target.value)
                    : setCustomPromo("");
                }}
                onClick={() => {
                  if (
                    !resetTextEntry &&
                    props.coupon &&
                    !couponIsFromAvailablePromotionsList
                  ) {
                    removePromo();
                    resetPromoAndReward();
                  } else {
                    setResetTextEntry(false);
                    handlePromoSubmit(customPromo, true);
                  }
                }}
              />
            </div>
          )}
        </div>
      </PromotionModal>

      <PromotionCheckoutModal
        title={
          (props.hasMoreDiscounts && props.coupon && props.successMsg) ||
          membershipDiscountIsApplied
            ? "You can save even more!"
            : "You can save today!"
        }
        checkoutButtonClick={props.checkoutButtonClick}
        checkoutButtonLabel={props.checkoutButtonLabel}
        open={props.checkoutPromoModalOpen}
        onClose={() => props.setCheckoutPromoModalOpen(false)}
      >
        <div className="mt-2.5 flex flex-col gap-3">
          {discountAndRewards(true)}
        </div>
      </PromotionCheckoutModal>

      {!!props.buyXgetYPromotionTitle && (
        <BuyXGetYCheckoutModal
          promotionText={props.buyXgetYPromotionTitle ?? ""}
          onFirstButtonClick={() => {
            handlePromoSubmit(props.buyXgetYPromoCode ?? "");
          }}
          onSecondButtonClick={props.checkoutButtonClick}
          open={!!props.buyXgetYPromotionTitle}
          onClose={() => props.setCheckoutPromoModalOpen(false)}
        />
      )}
    </div>
  );
};
